import { Button, Card, ListGroup, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useState, useEffect, useRef } from 'react';
import { useFirebase } from '../../../contexts/FirebaseContext';
import { useGaming } from '../../../contexts/GamingContext';
import { FullscreenLoader } from '../FullscreenLoader/FullscreenLoader';
import styles from './NotificationsList.module.scss';

const NotificationsList = ({
  isDropdown,
  userNotifications,
  fetchOthers,
  loading,
  finished,
  onUpdate,
}: {
  isDropdown: boolean;
  userNotifications?: any[];
  fetchOthers?: Function;
  loading?: boolean;
  finished?: boolean;
  onUpdate?: Function;
}) => {
  const { notifications, readAllUserNotifications, updateUserData } = useFirebase();
  const { openNotification } = useGaming();
  const { t } = useTranslation();

  // Local state for UI updates and loading state
  const [localNotifications, setLocalNotifications] = useState<any[]>([]);
  const [isUpdating, setIsUpdating] = useState(false);
  const isUpdatingRef = useRef(false);

  // Sync local state with props/context, but not when we're in the middle of an update
  useEffect(() => {
    if (isUpdatingRef.current) return; // Don't sync when we're updating
    
    const notificationSource = isDropdown ? notifications : userNotifications;
    if (notificationSource) {
      setLocalNotifications([...notificationSource]);
    }
  }, [notifications, userNotifications, isDropdown]);

  const notificationToVisualize = localNotifications;

  const readAll = async () => {
    setIsUpdating(true);
    isUpdatingRef.current = true;
    
    try {
      const updatedNotifications = localNotifications.map(notification => ({
        ...notification,
        read: true
      }));
      setLocalNotifications(updatedNotifications);
      
      await readAllUserNotifications();
    } catch (error) {
      console.error('Error marking notifications as read:', error);
      
      if (isDropdown) {
        try {
          await updateUserData();
        } catch (refreshError) {
          console.error('Error refreshing after failure:', refreshError);
        }
      }
    } finally {
      setIsUpdating(false);
      setTimeout(() => {
        isUpdatingRef.current = false;
      }, 500);
    }
  };

  const handleNotificationClick = async (notification: any) => {
    if (!notification.read) {
      const updatedNotifications = localNotifications.map(n => 
        n.id === notification.id ? { ...n, read: true } : n
      );
      setLocalNotifications(updatedNotifications);
      
      if (onUpdate) {
        onUpdate();
      }
    }
    
    openNotification(notification);
  };

  const renderReadAllTooltip = (props: any) => (
    <Tooltip id="read-all-tooltip" {...props}>
      {t('labels.leggiTutte')}
    </Tooltip>
  );

  const hasUnreadNotifications = notificationToVisualize.some(n => !n.read);

  return (
    <>
      {!isDropdown && loading && <FullscreenLoader fullscreen={false} />}

      {(isDropdown || !loading) && (
        <div className={`d-flex flex-column ${isDropdown ? styles.dropdown : ''}`}>
          {isDropdown && (
            <div className="notifications-header d-flex justify-content-between align-items-center px-3 py-2 border-bottom">
              <h6 className="mb-0">Notifiche</h6>
              {!!notificationToVisualize.length && hasUnreadNotifications && (
                <OverlayTrigger
                  placement="bottom"
                  delay={{ show: 250, hide: 400 }}
                  overlay={renderReadAllTooltip}
                >
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    className={`read-all-icon-btn p-1 ${styles.readAllButton}`}
                    onClick={readAll}
                    disabled={isUpdating}
                    style={{
                       opacity: isUpdating ? 0.6 : 1
                    }}
                  >
                    {isUpdating ? (
                      <span className={`spinner-border spinner-border-sm ${styles.smallSpinner}`} role="status" aria-hidden="true"></span>
                    ) : (
                      <span className={styles.readAllIcon}>✓✓</span>
                    )}
                  </Button>
                </OverlayTrigger>
              )}
            </div>
          )}

          {isDropdown && (!notificationToVisualize.length || !hasUnreadNotifications) && (
            <Card className={styles.notificationCard} bg={!isDropdown ? 'dark' : 'light'}>
              <Card.Body>{t('labels.noNotifiche')}</Card.Body>
            </Card>
          )}
          
          {!!notificationToVisualize.length &&
            !isDropdown &&
            notificationToVisualize.map((notification, i) => (
              <Card
                key={notification.id}
                className="listCard mb-2"
              >
                <Card.Body 
                  key={i.toString()} 
                  style={{ cursor: notification.data.form ? 'default' : 'pointer' }} 
                  onClick={() => handleNotificationClick(notification)}
                >
                  <div className={'d-flex flex-column flex-md-row align-items-start align-items-md-center'}>
                    <div className={notification.read ? '' : 'font-weight-bold'}>{notification.title}</div>
                    <small className="ml-md-auto">{notification.body}</small>
                  </div>
                </Card.Body>
              </Card>
            ))}

          <ListGroup variant={'flush'}>
            {isDropdown && !notificationToVisualize.length && <ListGroup.Item></ListGroup.Item>}
            {!!notificationToVisualize.length &&
              isDropdown &&
              hasUnreadNotifications &&
              notificationToVisualize.map((notification, i) => (
                <ListGroup.Item
                  key={notification.id}
                  style={{ cursor: notification.data.form ? 'default' : 'pointer' }}
                  className={notification.read ? styles.readNotification : styles.unreadNotification}
                  onClick={() => handleNotificationClick(notification)}
                >
                  <div>
                    <div className={'text-primary'}>{notification.title}</div>
                    <div className={'text-muted small'}>{notification.body}</div>
                  </div>
                </ListGroup.Item>
              ))}
            {isDropdown && (
              <ListGroup.Item>
                <Button
                  variant="primary"
                  className="w-100"
                  onClick={() => {
                    window.location.href = '/notifications';
                  }}
                >
                  {t('labels.visualizzaTutteNotifiche')}
                </Button>
              </ListGroup.Item>
            )}
          </ListGroup>
          
          <div className="d-flex justify-content-center">
            {!isDropdown && !finished && (
               <Button className="tootor-button tootor-button--secondary mt-3" onClick={() => fetchOthers()}>
                {t('labels.loadMore')}
              </Button>
            )}
            {!isDropdown && finished && <span className="mt-3">{t('labels.noMoreNotifications')}</span>}
          </div>
        </div>
      )}
    </>
  );
};

export { NotificationsList };
